import React, { useState, useRef, useEffect, useContext } from "react";
import { Container, Form, Button, Spinner } from "react-bootstrap";
import "../../../assests/css/studentdashboard/StudentCareer.css";
import StudentsideLayout from "../../../layouts/StudentsideLayout";
import axios from "axios";
import { AppContext } from "../../../context/Appcontext";

/* Suggested starter prompts shown only on the empty state */
const starters = [
  "I'm good at math and enjoy solving problems",
  "I like drawing and design, not sure what career fits",
  "I don't know what I'm good at yet",
  "What careers suit someone who likes helping people?",
];

export default function StudentCareerChat() {
  const loggedInStudent = JSON.parse(localStorage.getItem("student"));
  const { baseUrl } = useContext(AppContext);

  const [messages, setMessages] = useState([]); // { role: "user" | "ai", text }
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false); // waiting on an AI reply
  const [historyLoading, setHistoryLoading] = useState(true); // initial page-load fetch
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  /* Loads this student's saved chat history when the page opens */
  const fetchHistory = () => {
    if (!loggedInStudent?.id) {
      setHistoryLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("ngstudent_id", loggedInStudent.id);

    axios
      .post(`${baseUrl}/student/career/history`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        const past = response.data.messages.map((m) => ({
          role: m.role,
          text: m.message,
        }));
        setMessages(past);
        setHistoryLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setError("Couldn't load your previous conversation.");
        setHistoryLoading(false);
      });
  };

  /* Sends a new message; backend stores both the user message and the AI reply */
  const sendCareerMessage = (value) => {
    const formData = new FormData();
    formData.append("ngstudent_id", loggedInStudent.id);
    formData.append("message", value);

    axios
      .post(`${baseUrl}/student/career/chat`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        setMessages((prev) => [...prev, { role: "ai", text: response.data.reply }]);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setError("Something went wrong on my end. Please try again.");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = (text) => {
    const value = (text ?? input).trim();
    if (!value || loading || !loggedInStudent?.id) return;

    setError(null);
    setMessages((prev) => [...prev, { role: "user", text: value }]);
    setInput("");
    setLoading(true);

    sendCareerMessage(value);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const isEmpty = !historyLoading && messages.length === 0;

  return (
    <StudentsideLayout>
      <div className="cc-page">
        {/* ---------- Header ---------- */}
        <header className="cc-header">
          <Container className="cc-header-inner">
            <div className="cc-header-title">
              <span className="cc-header-dot" />
              Career Consultant
            </div>
            <span className="cc-header-sub">AI guidance, based on your skills &amp; interests</span>
          </Container>
        </header>

        {error && (
          <div className="cc-error-banner">
            <Container>{error}</Container>
          </div>
        )}

        {/* ---------- Message area ---------- */}
        <main className="cc-main" ref={scrollRef}>
          <Container className="cc-thread">
            {historyLoading ? (
              <div className="cc-history-loading">
                <Spinner animation="border" size="sm" />
                <span>Loading your conversation...</span>
              </div>
            ) : isEmpty ? (
              <div className="cc-empty">
                <h1 className="cc-empty-title">What are you good at?</h1>
                <p className="cc-empty-sub">
                  Tell me about your skills, subjects you enjoy, or what's on your mind
                  about the future — I'll help you figure out where it leads.
                </p>
                <div className="cc-starters">
                  {starters.map((s) => (
                    <button key={s} className="cc-starter-chip" onClick={() => send(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="cc-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`cc-row cc-row--${m.role}`}>
                    {m.role === "ai" && <div className="cc-avatar">AI</div>}
                    <div className={`cc-bubble cc-bubble--${m.role}`}>{m.text}</div>
                  </div>
                ))}
                {loading && (
                  <div className="cc-row cc-row--ai">
                    <div className="cc-avatar">AI</div>
                    <div className="cc-bubble cc-bubble--ai cc-bubble--typing">
                      <span className="cc-typing-dot" />
                      <span className="cc-typing-dot" />
                      <span className="cc-typing-dot" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </Container>
        </main>

        {/* ---------- Input bar ---------- */}
        <footer className="cc-composer">
          <Container>
            <Form
              className="cc-composer-inner"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <Form.Control
                as="textarea"
                ref={textareaRef}
                rows={1}
                placeholder="Message your career consultant..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                className="cc-input"
                disabled={historyLoading}
              />
              <Button
                type="submit"
                className="cc-send-btn"
                disabled={loading || historyLoading || !input.trim()}
              >
                {loading ? <Spinner animation="border" size="sm" /> : "Send"}
              </Button>
            </Form>
            <p className="cc-disclaimer">
              Guidance from AI can be wrong — always confirm big decisions with a mentor.
            </p>
          </Container>
        </footer>
      </div>
    </StudentsideLayout>
  );
}